// Dagstart als rooster (Joris, 25 sep: "kan de Dagstart ook zo ingedeeld worden als het
// Rooster?"). Puur: zet de Dagstart-data (taken per rij, inclusief de Outlook-afspraken en
// de agenda-uren uit dezelfde ronde) om naar de vorm die RoosterGrid tekent. Geen eigen
// ophaalronde — het rooster en de per-persoon-weergave tonen dus exact dezelfde data en
// dezelfde bezettingsgetallen.
//
// Anders dan in het Rooster zijn afspraken hier wél te zien (de dagstart gaat over de dag
// van het team): afspraken van een uur of langer staan als stil grijs blok, kortere als
// grijze vulling onderin de dag. Draaidagen en projectwerk zoals in het Rooster.

import { computeDayLoad, isDraaidagOptie } from '../../utils/capacityCalc';
import { disciplineVan, DISCIPLINES } from '../../utils/disciplines';
import {
  maakKolommen, kolomSjabloon, persoonBlokken, weekTotaal, agendaBlokTaken, weekendWerk, AGENDA_LABEL,
  WERKDAG_VENSTER,
} from '../PlannerTodo/rooster';
import { isZelfdeAfspraak } from '../../utils/draaidagenPlanning';
import { isKleineTaak } from '../../utils/dagstartLanes';
import { lokaleDatum } from '../PlannerTodo/plannerDatums';
import { werkSoort } from '../../utils/roosterBetekenis';

/** Vanaf zoveel uur krijgt een afspraak in de Dagstart een eigen (stil) blok. */
export const DAGSTART_BLOK_MIN_UREN = 1;

/** Kalenderdagen (incl. weekenden) van `startISO` t/m `eindISO`, in RoosterGrid-vorm. */
export function kolommenTussen(startISO, eindISO) {
  const start = lokaleDatum(startISO);
  const eind = String(eindISO || startISO).slice(0, 10);
  if (!start) return [];
  const dagen = Math.max(1, Math.round((lokaleDatum(eind) - start) / 864e5) + 1);
  return maakKolommen(start, Math.ceil(dagen / 7) + 1).filter((k) => k.iso <= eind);
}

/** Aantal weken voor de kolombreedtes (1/2/4) bij 5/10/20 werkdagen. */
export const wekenVoorBreedte = (werkdagen) => (werkdagen <= 5 ? 1 : werkdagen <= 10 ? 2 : 4);

const minuten = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ''));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

/**
 * Duur van een afspraak-taak in uren, begrensd tot de werkdag (08:00–18:00) — zoals de
 * capaciteit rekent: 07:00–09:00 telt als 1 u, niet 2. Zonder tijden: zijn `hours`.
 */
export function afspraakUren(t, venster = WERKDAG_VENSTER) {
  const s = minuten(t?.startTime);
  const e = minuten(t?.endTime);
  if (s !== null && e !== null && e > s) {
    return Math.max(0, Math.min(e, venster.eind) - Math.max(s, venster.begin)) / 60;
  }
  return Math.max(0, Number(t?.hours) || 0);
}

/**
 * Een Draaidag-taak uit de Dagstart als vast draaidagblok, zoals Planning → Rooster de
 * draaidagen uit de bron toont: op zijn echte datum (een draaidag op zondag schuift niet naar
 * maandag maar wordt een weekendmarkering), niet sleepbaar, en herkenbaar voor isZelfdeAfspraak.
 * Oranje zoals in Airtable; begint de titel met "OPTIE", dan gearceerd (nog niet bevestigd).
 */
export const alsDraaidagBlok = (t) => ({
  ...t,
  bronDraaidag: true,
  draaidagOptie: isDraaidagOptie(t),
  draaidagId: t.id,
  draaidagTitel: t.name,
  draaidagProjectnummer: t.projectNumber || '',
});

/** Afspraak-taken (uit Outlook of Airtable) als agenda-events voor de rooster-helpers. */
export function afsprakenAlsEvents(taken) {
  return (taken || [])
    .filter((t) => t?.type === 'Afspraak' && t.startDate)
    .map((t) => ({
      date: String(t.startDate).slice(0, 10),
      subject: t.afgeschermd ? 'Afspraak' : (t.name || 'Afspraak'),
      start_time: t.startTime || '',
      end_time: t.endTime || '',
      hours: afspraakUren(t),
      prive: !!t.prive,
      afgeschermd: !!t.afgeschermd,
      // Voor het afsprakenpaneel (3 nov): soort en van wie.
      soort: t.afspraakSoort || '',
      assignee: t.assignee || '',
    }));
}

/**
 * Het agendadeel in de opbouw verkleinen met de uren die al als blok op de rij staan, zodat
 * de grijze vulling alleen de rest toont (de bezetting zelf blijft ongemoeid).
 */
export function opbouwZonderBlokken(opbouw, blokUrenPerDag) {
  if (!opbouw || !Object.keys(blokUrenPerDag || {}).length) return opbouw;
  const uit = { ...opbouw };
  Object.entries(blokUrenPerDag).forEach(([d, af]) => {
    if (!uit[d]) return;
    let rest = af;
    uit[d] = uit[d].map((r) => {
      if (r.label !== AGENDA_LABEL || rest <= 0) return r;
      const eraf = Math.min(r.uren, rest);
      rest -= eraf;
      return { ...r, uren: r.uren - eraf };
    }).filter((r) => r.uren > 0.005);
  });
  return uit;
}

/**
 * Alles wat RoosterGrid per persoon nodig heeft, uit de Dagstart-data.
 *
 * @param rijen         zichtbare Dagstart-rijen [{ naam, functie, persoonId }]
 * @param takenPerNaam  { naam: taak[] } (al zonder verborgen privé-taken)
 * @param agendaUren    { naam: { 'YYYY-MM-DD': uren } }
 * @param kolommen      kolommenTussen(start, eind)
 * @param rules         capaciteitsregels
 */
export function bouwDagstartRooster({
  rijen, takenPerNaam, agendaUren, kolommen, rules, horizonPerNaam = {}, horizonDagen = [], verbergKlein = false,
  zonderAgendaBlokken = false,
}) {
  const dayISOs = kolommen.filter((k) => !k.weekend).map((k) => k.iso);
  // Zelfde rekenhorizon als PersonTimeline (#62): taken die na het venster doorlopen krijgen
  // daar hun uren; getoond worden alleen de dagen in beeld.
  const rekenDagen = [...dayISOs, ...(horizonDagen || []).filter((d) => !dayISOs.includes(d))];
  const hoursPerDay = rules?.hoursPerDay || 8;
  const persons = [];
  const labels = {};
  const belasting = {};
  const blokkenPerPersoon = {};
  const agendaPerPersoon = {};
  const weekendPerPersoon = {};
  const takenPerPersoon = {};
  (rijen || []).forEach((a) => {
    const id = a.naam;
    const taken = takenPerNaam?.[a.naam] || [];
    persons.push({ id, name: a.naam, function: a.functie || a.function || '', persoonId: a.persoonId || null });
    labels[id] = a.naam;
    takenPerPersoon[id] = taken;

    const events = afsprakenAlsEvents(taken);
    const draaidagen = taken.filter((t) => t.type === 'Draaidag').map(alsDraaidagBlok);
    // Kleine taken verbergen (≤ 15 min) haalt alleen het blok weg; de bezetting hieronder
    // rekent ze mee — hun uren vallen dan in de grijze vulling van de dag.
    const overig = taken.filter((t) => t.type !== 'Draaidag' && !(verbergKlein && isKleineTaak(t)));
    // Per persoon staan de afspraken met tijden in de lane eronder; dan niet ook als blok
    // (hun uren vallen gewoon in de bezetting).
    const blokAfspraken = zonderAgendaBlokken ? [] : agendaBlokTaken(events, {
      draaidagen, zelfde: isZelfdeAfspraak, minUren: DAGSTART_BLOK_MIN_UREN,
    });
    const blokUren = {};
    blokAfspraken.forEach((b) => { blokUren[b.startDate] = (blokUren[b.startDate] || 0) + (Number(b.hours) || 0); });

    // Zelfde som de per-persoon-weergave (PersonTimeline): taken + agenda-uren uit dezelfde ronde.
    const ids = new Set(taken.map((t) => t.id));
    const metHorizon = [...taken, ...(horizonPerNaam?.[a.naam] || []).filter((t) => !ids.has(t.id))];
    const load = computeDayLoad(metHorizon, rekenDagen, agendaUren?.[a.naam] || {}, rules);
    belasting[id] = {
      load: { ...load, opbouw: opbouwZonderBlokken(load.opbouw, blokUren) },
      totaal: weekTotaal(load.uren, load.staat, dayISOs, hoursPerDay),
    };
    // Eén vaste lane per project (Joris 6 okt): 9689 op ma–di en vr staat op één regel.
    // Joris 7 okt: kleur = soort werk, dus een blok bevat één soort werk (script en animatie van
    // hetzelfde project worden twee blokken op dezelfde regel, elk in zijn eigen kleur).
    blokkenPerPersoon[id] = persoonBlokken([...overig, ...draaidagen, ...blokAfspraken], kolommen, { laneProProject: true, groep: werkSoort });

    const perDag = {};
    events.forEach((ev) => { (perDag[ev.date] = perDag[ev.date] || []).push(ev); });
    agendaPerPersoon[id] = perDag;
    const w = weekendWerk(events, draaidagen, kolommen, { zelfde: isZelfdeAfspraak });
    if (Object.keys(w).length) weekendPerPersoon[id] = w;
  });
  return {
    persons, labels, belasting, blokkenPerPersoon, agendaPerPersoon, weekendPerPersoon, dayISOs,
    existingTasks: takenPerPersoon,
  };
}

/** Personen per discipline (de teamtabs van de Dagstart), in de volgorde van de tabs. */
export function groepeerOpDiscipline(persons, disciplinePerNaam = {}) {
  const uit = {};
  DISCIPLINES.forEach((d) => { uit[d.label] = []; });
  (persons || []).forEach((p) => {
    const key = disciplinePerNaam[p.name] || disciplineVan({ name: p.name, function: p.function });
    const d = DISCIPLINES.find((x) => x.key === key) || DISCIPLINES[DISCIPLINES.length - 1];
    uit[d.label].push(p);
  });
  Object.keys(uit).forEach((k) => { if (!uit[k].length) delete uit[k]; });
  return uit;
}

export { kolomSjabloon };
